// Team Management Types

export type UserRole = 'super_admin' | 'team_leader' | 'team_member' | 'team_admin';

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export type SubscriptionPlan = 'standard' | 'team' | 'business' | 'personal_pro';

export interface Organization {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  owner_id: string;
  subscription_plan: SubscriptionPlan;
  max_members: number;
  company_name?: string;
  company_registration?: string;
  vat_number?: string;
  is_active: boolean;
}

export interface OrganizationMember {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  user_id: string;
  role: UserRole;
  joined_at: string;
  is_active: boolean;
  // Joined from auth.users
  email?: string;
  user_metadata?: {
    first_name?: string;
    last_name?: string;
    assessor_type?: string;
  };
}

export interface TeamInvitation {
  id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  invited_by: string;
  invited_email: string;
  invited_role: UserRole;
  invitation_token: string;
  expires_at: string;
  accepted_at?: string;
  status: InvitationStatus;
  invited_user_id?: string;
}

export interface TeamInvitationWithDetails extends TeamInvitation {
  invited_by_name?: string;
  organization_name?: string;
}

export interface PlanLimits {
  plan: SubscriptionPlan;
  maxMembers: number;
  displayName: string;
}

export const PLAN_LIMITS: Record<SubscriptionPlan, PlanLimits> = {
  standard: {
    plan: 'standard',
    maxMembers: 1,
    displayName: 'Standard'
  },
  team: {
    plan: 'team',
    maxMembers: 5,
    displayName: 'Team'
  },
  business: {
    plan: 'business',
    maxMembers: 10,
    displayName: 'Business'
  },
  personal_pro: {
    plan: 'personal_pro',
    maxMembers: 1,
    displayName: 'Personal Pro'
  }
};

// Role Permissions
export interface RolePermissions {
  canViewAllFiles: boolean;
  canDeleteMembers: boolean;
  canManageBilling: boolean;
  canPromoteUsers: boolean;
  canCreateSurveys: boolean;
  canAccessCalendar: boolean;
  canAccessChat: boolean;
  countsTowardLimit: boolean;
}

export interface RoleMetadata extends RolePermissions {
  displayName: string;
  description: string;
  icon: string;
}

export const ROLE_METADATA: Record<UserRole, RoleMetadata> = {
  super_admin: {
    displayName: 'Super Admin',
    description: 'Full access to all features and settings. Organization owner.',
    icon: '👑',
    canViewAllFiles: true,
    canDeleteMembers: true,
    canManageBilling: true,
    canPromoteUsers: true,
    canCreateSurveys: true,
    canAccessCalendar: true,
    canAccessChat: true,
    countsTowardLimit: false
  },
  team_leader: {
    displayName: 'Team Leader',
    description: 'Can use all features and view team files, but cannot manage members or billing.',
    icon: '⭐',
    canViewAllFiles: true,
    canDeleteMembers: false,
    canManageBilling: false,
    canPromoteUsers: false,
    canCreateSurveys: true,
    canAccessCalendar: true,
    canAccessChat: true,
    countsTowardLimit: true
  },
  team_member: {
    displayName: 'Team Member',
    description: 'Can create surveys and view own files only.',
    icon: '👤',
    canViewAllFiles: false,
    canDeleteMembers: false,
    canManageBilling: false,
    canPromoteUsers: false,
    canCreateSurveys: true,
    canAccessCalendar: true,
    canAccessChat: true,
    countsTowardLimit: true
  },
  team_admin: {
    displayName: 'Team Admin',
    description: 'Limited access to calendar, chat, and settings only. Does not count toward team limit.',
    icon: '📋',
    canViewAllFiles: false,
    canDeleteMembers: false,
    canManageBilling: false,
    canPromoteUsers: false,
    canCreateSurveys: false,
    canAccessCalendar: true,
    canAccessChat: true,
    countsTowardLimit: false
  }
};
